import { FC, useEffect, useRef, useState } from 'react';
import { Input, Modal, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { useFelaEnhanced } from 'hooks';
import { useGlobalDialog } from 'hooks/useGlobalDialog';
import { DialogState } from 'constants/common';
import { Button } from 'modules/ui';
import { Select, FloatingLabel } from 'modules/form';
import { useNotifications } from 'modules/ui';
import { useUpdateIncident } from 'modules/entities/modules/incidents';
import { CASE_STATUS_OPTIONS, CASE_STATUS_SUBSTATUS_MAP } from 'modules/entities/modules/incidents/constants';

import * as felaRules from './EditIncidentDialog.rules';

const { TextArea } = Input;

interface EditIncidentMeta {
    caseNumber: string;
    description?: string;
    status?: string;
    subStatus?: string;
}

export const EditIncidentDialog: FC = () => {
    const { styles } = useFelaEnhanced(felaRules);
    const { formatMessage } = useIntl();
    const notifications = useNotifications();
    const containerRef = useRef<HTMLDivElement>(null);
    const { state, navigate, meta } = useGlobalDialog();
    const editMeta = meta as EditIncidentMeta;

    const [description, setDescription] = useState('');
    const [status, setStatus] = useState<string>(null);
    const [subStatus, setSubStatus] = useState<string>(null);

    const isOpen = state === DialogState.EDIT_INCIDENT;

    const { api, updateIncident, resetUpload } = useUpdateIncident(editMeta?.caseNumber);

    useEffect(() => {
        if (isOpen && editMeta) {
            setDescription(editMeta.description ?? '');
            setStatus(editMeta.status ?? null);
            setSubStatus(editMeta.subStatus ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, editMeta?.caseNumber]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (api?.success) {
            notifications.success({
                key: 'edit-incident',
                message: formatMessage({ id: 'page.incidents.table.edit.notification.success.title' }),
                description: formatMessage({ id: 'page.incidents.table.edit.notification.success.message' }),
            });
            resetUpload();
            navigate(null);
        } else if (api?.error) {
            notifications.error({
                key: 'edit-incident',
                message: formatMessage({ id: 'page.incidents.table.edit.notification.error.title' }),
                description: formatMessage({ id: 'page.incidents.table.edit.notification.error.message' }),
            });
            resetUpload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api?.success, api?.error, isOpen]);

    const subStatusOptions = status ? CASE_STATUS_SUBSTATUS_MAP[status] ?? [] : [];
    const requiresSubStatus = subStatusOptions.length > 0;

    const handleClose = () => {
        navigate(null);
    };

    const handleSubmit = () => {
        updateIncident({
            payload: {
                description,
                status,
                subStatus: requiresSubStatus ? subStatus : '',
            },
        });
    };

    return (
        <div ref={containerRef}>
            <Modal
                title={null}
                footer={null}
                closable={false}
                maskClosable={false}
                visible={isOpen}
                getContainer={() => containerRef.current}
                className={styles.modal}
                width={738}
                onCancel={handleClose}
            >
                <div data-testid="edit-incident-dialog">
                    <div className={styles.header}>
                        <Typography.Title level={2} className={styles.title}>
                            <FormattedMessage id="page.incidents.table.edit.dialog.title" />
                        </Typography.Title>
                    </div>
                    <div className={styles.content}>
                        <div className={styles.fieldRow}>
                            <TextArea
                                id="edit-incident-description"
                                className={styles.textArea}
                                value={description}
                                autoSize={{ minRows: 3, maxRows: 5 }}
                                placeholder={formatMessage({
                                    id: 'page.incidents.table.edit.dialog.description.placeholder',
                                })}
                                onChange={({ target }) => setDescription(target.value)}
                            />
                        </div>

                        <div className={styles.fieldRow}>
                            <div className={styles.componentRow}>
                                <FloatingLabel
                                    htmlFor="edit-incident-status"
                                    floating={Boolean(status)}
                                    label={formatMessage({ id: 'page.incidents.table.edit.dialog.status.label' })}
                                >
                                    {({ onFocus, onBlur }) => (
                                        <Select
                                            id="edit-incident-status"
                                            value={status}
                                            onFocus={onFocus}
                                            onBlur={onBlur}
                                            onChange={(value: string) => {
                                                setStatus(value);
                                                setSubStatus(null);
                                            }}
                                            options={CASE_STATUS_OPTIONS.map(value => ({ value, label: value }))}
                                            placeholder={formatMessage({
                                                id: 'page.incidents.table.edit.dialog.status.placeholder',
                                            })}
                                            fullWidth
                                            floatingLabel
                                        />
                                    )}
                                </FloatingLabel>
                                <FloatingLabel
                                    htmlFor="edit-incident-substatus"
                                    floating={Boolean(subStatus)}
                                    label={formatMessage({ id: 'page.incidents.table.edit.dialog.subStatus.label' })}
                                >
                                    {({ onFocus, onBlur }) => (
                                        <Select
                                            id="edit-incident-substatus"
                                            value={subStatus}
                                            onFocus={onFocus}
                                            onBlur={onBlur}
                                            onChange={setSubStatus}
                                            options={subStatusOptions.map(value => ({ value, label: value }))}
                                            disabled={!requiresSubStatus}
                                            placeholder={formatMessage({
                                                id: 'page.incidents.table.edit.dialog.subStatus.placeholder',
                                            })}
                                            fullWidth
                                            floatingLabel
                                        />
                                    )}
                                </FloatingLabel>
                            </div>
                        </div>

                        <div className={styles.bottomButtons}>
                            <Button size="large" onClick={handleClose}>
                                <FormattedMessage id="page.incidents.table.validation.dialog.cancel" />
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                disabled={api?.inProgress || !status || (requiresSubStatus && !subStatus)}
                                onClick={handleSubmit}
                            >
                                <FormattedMessage id="page.incidents.table.edit.dialog.submit" />
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
